/**
 * Template Builder Page - Configurazione Template Report
 * Il Diario di Bordo - Frontend
 * 
 * Permette ai coordinatori di creare e modificare template custom.
 * 
 * Funzionalità:
 * - Lista sezioni con toggle on/off
 * - Drag & drop per riordinare sezioni
 * - Opzioni specifiche per ogni sezione
 * - Salvataggio template con nome
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { reportsApi } from '../../services/api'
import { Card } from '../../components/ui'
import {
  Save, ArrowLeft, GripVertical, Eye, EyeOff, Settings,
  ChevronDown, ChevronUp, Loader2, FileText, BarChart3,
  Calendar, Table, Users, MessageSquare, CheckSquare,
  Layout, FileSpreadsheet, Footprints, Plus, Copy, Trash2
} from 'lucide-react'

// === CONFIGURAZIONE SEZIONI ===

const AVAILABLE_SECTIONS = [
  {
    id: 'header',
    name: 'Header',
    description: 'Logo app, nome centro, info contatto',
    icon: Layout,
    defaultEnabled: true,
    options: [
      { key: 'showAddress', label: 'Mostra indirizzo', type: 'boolean', default: true },
      { key: 'showPhone', label: 'Mostra telefono', type: 'boolean', default: true },
      { key: 'showEmail', label: 'Mostra email', type: 'boolean', default: false }
    ]
  },
  {
    id: 'anagrafica',
    name: 'Anagrafica Minore',
    description: 'Dati anagrafici del minore',
    icon: Users,
    defaultEnabled: true,
    options: [
      { 
        key: 'fields', 
        label: 'Campi da mostrare', 
        type: 'multiselect',
        choices: [
          { value: 'nome', label: 'Nome' },
          { value: 'cognome', label: 'Cognome' },
          { value: 'dataNascita', label: 'Data di nascita' },
          { value: 'codiceFiscale', label: 'Codice fiscale' },
          { value: 'scuola', label: 'Scuola' },
          { value: 'classe', label: 'Classe' },
          { value: 'dataInserimento', label: 'Data inserimento' },
          { value: 'educatoreReferente', label: 'Educatore referente' }
        ],
        default: ['nome', 'cognome', 'dataNascita', 'scuola', 'classe']
      },
      { 
        key: 'layout', 
        label: 'Layout', 
        type: 'select',
        choices: [
          { value: 'grid', label: 'Griglia (3 colonne)' },
          { value: 'list', label: 'Lista verticale' }
        ],
        default: 'grid'
      }
    ]
  },
  {
    id: 'presenze_stats',
    name: 'Statistiche Presenze',
    description: 'Riepilogo numerico presenze/assenze',
    icon: BarChart3,
    defaultEnabled: true,
    options: [
      { 
        key: 'format', 
        label: 'Formato', 
        type: 'select',
        choices: [
          { value: 'cards', label: 'Cards colorate' },
          { value: 'table', label: 'Tabella semplice' }
        ],
        default: 'cards'
      },
      { key: 'showPercentage', label: 'Mostra percentuale', type: 'boolean', default: true },
      { key: 'showTrend', label: 'Mostra trend vs periodo precedente', type: 'boolean', default: true },
      { key: 'showHours', label: 'Mostra ore totali', type: 'boolean', default: false }
    ]
  },
  {
    id: 'presenze_calendario',
    name: 'Calendario Presenze',
    description: 'Vista calendario mensile con colori',
    icon: Calendar,
    defaultEnabled: true,
    options: [
      { 
        key: 'style', 
        label: 'Stile', 
        type: 'select',
        choices: [
          { value: 'monthly', label: 'Calendario mensile' },
          { value: 'compact', label: 'Vista compatta' }
        ],
        default: 'monthly'
      },
      { key: 'showLegend', label: 'Mostra legenda', type: 'boolean', default: true },
      { 
        key: 'colorScheme', 
        label: 'Schema colori', 
        type: 'select',
        choices: [
          { value: 'default', label: 'Standard' },
          { value: 'colorblind', label: 'Accessibile (daltonici)' }
        ],
        default: 'default'
      }
    ]
  },
  {
    id: 'comportamenti_grafico',
    name: 'Grafico Comportamenti',
    description: 'Grafici trend e distribuzione comportamenti',
    icon: BarChart3,
    defaultEnabled: true,
    options: [
      { 
        key: 'chartType', 
        label: 'Tipo grafico', 
        type: 'select',
        choices: [
          { value: 'line', label: 'Linea (trend)' },
          { value: 'bar', label: 'Barre' },
          { value: 'doughnut', label: 'Ciambella' }
        ],
        default: 'line'
      },
      { key: 'showTrend', label: 'Mostra trend temporale', type: 'boolean', default: true },
      { key: 'periodComparison', label: 'Confronto periodo precedente', type: 'boolean', default: true }
    ]
  },
  {
    id: 'comportamenti_tabella',
    name: 'Tabella Comportamenti',
    description: 'Dettaglio comportamenti con frequenza',
    icon: Table,
    defaultEnabled: true,
    options: [
      { key: 'showTrend', label: 'Colonna trend', type: 'boolean', default: true },
      { key: 'showPercentage', label: 'Colonna percentuale', type: 'boolean', default: true },
      { key: 'showFrequency', label: 'Colonna frequenza', type: 'boolean', default: true },
      { 
        key: 'sortBy', 
        label: 'Ordina per', 
        type: 'select',
        choices: [
          { value: 'frequency', label: 'Frequenza (decrescente)' },
          { value: 'alphabetical', label: 'Alfabetico' },
          { value: 'trend', label: 'Variazione trend' }
        ],
        default: 'frequency'
      }
    ]
  },
  {
    id: 'attivita',
    name: 'Attività Svolte',
    description: 'Compiti, attività gruppo, tempo libero',
    icon: CheckSquare,
    defaultEnabled: true,
    options: [
      { key: 'includeCompiti', label: 'Includi compiti', type: 'boolean', default: true },
      { key: 'includeGruppo', label: 'Includi attività gruppo', type: 'boolean', default: true },
      { key: 'includeTempoLibero', label: 'Includi tempo libero', type: 'boolean', default: true },
      { key: 'includeCustomSections', label: 'Includi sezioni personalizzate', type: 'boolean', default: true },
      { key: 'showCounts', label: 'Mostra conteggi', type: 'boolean', default: true },
      { 
        key: 'layout', 
        label: 'Layout', 
        type: 'select',
        choices: [
          { value: 'grid', label: 'Griglia' },
          { value: 'list', label: 'Lista' }
        ],
        default: 'grid'
      }
    ]
  },
  {
    id: 'attivita_custom',
    name: 'Sezioni Personalizzate',
    description: 'Statistiche sezioni custom del centro',
    icon: Settings,
    defaultEnabled: false,
    options: [
      { key: 'showStatistics', label: 'Mostra statistiche', type: 'boolean', default: true },
      { key: 'includeNotes', label: 'Includi note associate', type: 'boolean', default: true }
    ]
  },
  {
    id: 'testo_libero',
    name: 'Note e Osservazioni',
    description: 'Campo testo compilato dall\'utente',
    icon: MessageSquare,
    defaultEnabled: true,
    options: [
      { 
        key: 'sourceFields', 
        label: 'Campi sorgente', 
        type: 'multiselect',
        choices: [
          { value: 'descrizioneGiornata', label: 'Descrizione giornata' },
          { value: 'descrizioneComportamenti', label: 'Descrizione comportamenti' },
          { value: 'interventiEducativi', label: 'Interventi educativi' },
          { value: 'situazioneFamiliare', label: 'Situazione familiare' }
        ],
        default: ['descrizioneGiornata']
      },
      { key: 'title', label: 'Titolo sezione', type: 'text', default: 'Note e Osservazioni' },
      { key: 'showSourceData', label: 'Mostra dati originali', type: 'boolean', default: true }
    ]
  },
  {
    id: 'sintesi',
    name: 'Sintesi Valutativa',
    description: 'Valutazione compilata dall\'utente',
    icon: FileText,
    defaultEnabled: true,
    options: [
      { key: 'includeAutoSummary', label: 'Includi statistiche automatiche', type: 'boolean', default: true }
    ]
  },
  {
    id: 'footer',
    name: 'Footer',
    description: 'Firma, timestamp, riferimento',
    icon: Footprints,
    defaultEnabled: true,
    options: [
      { key: 'showSignature', label: 'Spazio firma', type: 'boolean', default: true },
      { key: 'signatureLabel', label: 'Etichetta firma', type: 'text', default: 'Il Coordinatore' },
      { key: 'showTimestamp', label: 'Data/ora generazione', type: 'boolean', default: true },
      { key: 'showReference', label: 'Codice riferimento', type: 'boolean', default: true },
      { key: 'customText', label: 'Testo personalizzato', type: 'text', default: '' }
    ]
  }
]

// === COMPONENTE SEZIONE DRAGGABLE ===

function SectionItem({ 
  section, 
  sectionConfig, 
  index, 
  totalSections,
  onToggle, 
  onOptionsChange, 
  onMoveUp, 
  onMoveDown,
  isExpanded,
  onToggleExpand 
}) {
  const Icon = section.icon
  
  return (
    <div className={`
      border rounded-lg overflow-hidden transition-all
      ${sectionConfig.enabled 
        ? 'border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-800' 
        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 opacity-60'
      }
    `}>
      {/* Header sezione */}
      <div className="flex items-center gap-3 p-3">
        {/* Drag handle */}
        <div className="cursor-grab text-gray-400 hover:text-gray-600">
          <GripVertical className="w-5 h-5" />
        </div>
        
        {/* Icona */}
        <div className={`
          p-2 rounded-lg
          ${sectionConfig.enabled 
            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
            : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
          }
        `}>
          <Icon className="w-5 h-5" />
        </div>
        
        {/* Nome e descrizione */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-medium ${sectionConfig.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500'}`}>
            {section.name}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {section.description}
          </p>
        </div>
        
        {/* Controlli */}
        <div className="flex items-center gap-2">
          {/* Ordine */}
          <div className="flex flex-col">
            <button
              onClick={() => onMoveUp(index)}
              disabled={index === 0}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
              title="Sposta su"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
            <button
              onClick={() => onMoveDown(index)}
              disabled={index === totalSections - 1}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
              title="Sposta giù"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          </div>
          
          {/* Toggle opzioni */}
          {section.options?.length > 0 && sectionConfig.enabled && (
            <button
              onClick={() => onToggleExpand(section.id)}
              className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
              title="Opzioni sezione"
            >
              <Settings className="w-4 h-4" />
            </button>
          )}
          
          {/* Toggle enabled */}
          <button
            onClick={() => onToggle(section.id)}
            className={`
              p-2 rounded transition-colors
              ${sectionConfig.enabled 
                ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' 
                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }
            `}
            title={sectionConfig.enabled ? 'Disabilita sezione' : 'Abilita sezione'}
          >
            {sectionConfig.enabled ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {/* Opzioni espandibili */}
      {isExpanded && sectionConfig.enabled && section.options?.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.options.map(option => (
              <OptionField
                key={option.key}
                option={option}
                value={sectionConfig.options?.[option.key] ?? option.default}
                onChange={(value) => onOptionsChange(section.id, option.key, value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// === COMPONENTE CAMPO OPZIONE ===

function OptionField({ option, value, onChange }) {
  switch (option.type) {
    case 'boolean':
      return (
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
        </label>
      )
    
    case 'select':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {option.label}
          </label>
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500"
          >
            {option.choices.map(choice => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
        </div>
      )
    
    case 'multiselect':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {option.label}
          </label>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {option.choices.map(choice => (
              <label key={choice.value} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value || []).includes(choice.value)}
                  onChange={(e) => {
                    const currentValues = value || []
                    if (e.target.checked) {
                      onChange([...currentValues, choice.value])
                    } else {
                      onChange(currentValues.filter(v => v !== choice.value))
                    }
                  }}
                  className="w-3 h-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-600 dark:text-gray-400">{choice.label}</span>
              </label>
            ))}
          </div>
        </div>
      )
    
    case 'text':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {option.label}
          </label>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                       bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                       focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )
    
    default:
      return null
  }
}

// === COMPONENTE PRINCIPALE ===

export default function TemplateBuilderPage() {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const { user, tenant } = useAuth()
  const { showSuccess, showError } = useNotification()
  
  const isEditing = !!templateId && templateId !== 'new'
  
  // State
  const [loading, setLoading] = useState(isEditing)
  const [saving, setSaving] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [templateDescription, setTemplateDescription] = useState('')
  const [targetType, setTargetType] = useState('minore')
  const [expandedSections, setExpandedSections] = useState(new Set())
  
  // Configurazione sezioni
  const [sectionsConfig, setSectionsConfig] = useState(() => {
    // Inizializza con default
    return AVAILABLE_SECTIONS.map((section, idx) => ({
      id: section.id,
      enabled: section.defaultEnabled,
      order: idx + 1,
      options: section.options?.reduce((acc, opt) => {
        acc[opt.key] = opt.default
        return acc
      }, {}) || {}
    }))
  })
  
  // Opzioni globali
  const [globalOptions, setGlobalOptions] = useState({
    orientation: 'portrait',
    paperSize: 'A4',
    includeAppLogo: true,
    pageNumbers: true,
    primaryColor: null
  })
  
  // Carica template esistente
  useEffect(() => {
    if (isEditing) {
      loadTemplate()
    }
  }, [templateId])
  
  const loadTemplate = async () => {
    try {
      const response = await reportsApi.getTemplate(templateId)
      const template = response.data?.data
      
      if (template) {
        setTemplateName(template.name)
        setTemplateDescription(template.description || '')
        setTargetType(template.targetType || 'minore')
        setGlobalOptions(template.globalOptions || globalOptions)
        
        // Mappa sezioni dal template
        if (template.sections) {
          setSectionsConfig(prev => {
            // Crea mappa delle sezioni esistenti
            const templateSectionsMap = {}
            template.sections.forEach(s => {
              templateSectionsMap[s.id] = s
            })
            
            // Aggiorna configurazione mantenendo l'ordine del template
            const updated = prev.map(section => {
              const templateSection = templateSectionsMap[section.id]
              if (templateSection) {
                return {
                  ...section,
                  enabled: templateSection.enabled,
                  order: templateSection.order,
                  options: { ...section.options, ...templateSection.options }
                }
              }
              return section
            })
            
            // Ordina per order
            return updated.sort((a, b) => a.order - b.order)
          })
        }
      }
    } catch (error) {
      console.error('Error loading template:', error)
      showError('Errore nel caricamento del template')
    } finally {
      setLoading(false)
    }
  }
  
  // Toggle sezione enabled
  const toggleSection = useCallback((sectionId) => {
    setSectionsConfig(prev => prev.map(s => 
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    ))
  }, [])
  
  // Aggiorna opzioni sezione
  const updateSectionOptions = useCallback((sectionId, optionKey, value) => {
    setSectionsConfig(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, options: { ...s.options, [optionKey]: value } }
        : s
    ))
  }, [])
  
  // Sposta sezione su
  const moveSectionUp = useCallback((index) => {
    if (index === 0) return
    setSectionsConfig(prev => {
      const newConfig = [...prev]
      const temp = newConfig[index - 1]
      newConfig[index - 1] = { ...newConfig[index], order: index }
      newConfig[index] = { ...temp, order: index + 1 }
      return newConfig
    })
  }, [])
  
  // Sposta sezione giù
  const moveSectionDown = useCallback((index) => {
    setSectionsConfig(prev => {
      if (index === prev.length - 1) return prev
      const newConfig = [...prev]
      const temp = newConfig[index + 1]
      newConfig[index + 1] = { ...newConfig[index], order: index + 2 }
      newConfig[index] = { ...temp, order: index + 1 }
      return newConfig
    })
  }, [])
  
  // Toggle espansione opzioni
  const toggleExpand = useCallback((sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }, [])
  
  // Salva template
  const handleSave = async () => {
    if (!templateName.trim()) {
      showError('Inserisci un nome per il template')
      return
    }
    
    const enabledCount = sectionsConfig.filter(s => s.enabled).length
    if (enabledCount === 0) {
      showError('Abilita almeno una sezione')
      return
    }
    
    setSaving(true)
    
    try {
      const templateData = {
        name: templateName.trim(),
        description: templateDescription.trim(),
        type: 'custom',
        targetType,
        sections: sectionsConfig.map((s, idx) => ({
          id: s.id,
          enabled: s.enabled,
          order: idx + 1,
          options: s.options
        })),
        globalOptions
      }
      
      if (isEditing) {
        await reportsApi.updateTemplate(templateId, templateData)
        showSuccess('Template aggiornato!')
      } else {
        await reportsApi.createTemplate(templateData)
        showSuccess('Template creato!')
      }
      
      navigate('/reports')
    } catch (error) {
      console.error('Error saving template:', error)
      const message = error.response?.data?.message || 'Errore nel salvataggio'
      showError(message)
    } finally {
      setSaving(false)
    }
  }
  
  // Duplica da template di sistema
  const handleDuplicateFrom = async (systemTemplateId) => {
    try {
      const response = await reportsApi.getTemplate(systemTemplateId)
      const template = response.data?.data
      
      if (template) {
        setTemplateName(`${template.name} (Copia)`)
        setTemplateDescription(template.description || '')
        setTargetType(template.targetType || 'minore')
        setGlobalOptions(template.globalOptions || globalOptions)
        
        if (template.sections) {
          const templateSectionsMap = {}
          template.sections.forEach(s => {
            templateSectionsMap[s.id] = s
          })
          
          setSectionsConfig(prev => {
            return prev.map(section => {
              const templateSection = templateSectionsMap[section.id]
              if (templateSection) {
                return {
                  ...section,
                  enabled: templateSection.enabled,
                  order: templateSection.order,
                  options: { ...section.options, ...templateSection.options }
                }
              }
              return section
            }).sort((a, b) => a.order - b.order)
          })
        }
        
        showSuccess('Template copiato! Modifica e salva.')
      }
    } catch (error) {
      showError('Errore nella copia del template')
    }
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }
  
  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/reports')}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditing ? 'Modifica Template' : 'Nuovo Template'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Configura le sezioni e le opzioni del report
            </p>
          </div>
        </div>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Salva Template
        </button>
      </div>
      
      {/* Form info base */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Informazioni Template
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nome template *
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Es: Report mensile dettagliato"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tipo di report
            </label>
            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500"
            >
              <option value="minore">Report Minore</option>
              <option value="centro">Report Centro (tutti i minori)</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descrizione (opzionale)
            </label>
            <textarea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              placeholder="Descrivi quando usare questo template..."
              rows={2}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>
      
      {/* Opzioni globali */}
      <Card className="p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Opzioni Documento
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Orientamento
            </label>
            <select
              value={globalOptions.orientation}
              onChange={(e) => setGlobalOptions(prev => ({ ...prev, orientation: e.target.value }))}
              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="portrait">Verticale</option>
              <option value="landscape">Orizzontale</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Formato
            </label>
            <select
              value={globalOptions.paperSize}
              onChange={(e) => setGlobalOptions(prev => ({ ...prev, paperSize: e.target.value }))}
              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="A4">A4</option>
              <option value="Letter">Letter</option>
            </select>
          </div>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={globalOptions.includeAppLogo}
              onChange={(e) => setGlobalOptions(prev => ({ ...prev, includeAppLogo: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Logo app</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={globalOptions.pageNumbers}
              onChange={(e) => setGlobalOptions(prev => ({ ...prev, pageNumbers: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Numeri pagina</span>
          </label>
        </div>
      </Card>
      
      {/* Configurazione sezioni */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Sezioni del Report
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Abilita/disabilita e riordina le sezioni. Clicca ⚙️ per le opzioni.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {sectionsConfig.filter(s => s.enabled).length} / {sectionsConfig.length} attive
          </div>
        </div>
        
        <div className="space-y-3">
          {sectionsConfig.map((sectionConfig, index) => {
            const section = AVAILABLE_SECTIONS.find(s => s.id === sectionConfig.id)
            if (!section) return null
            
            return (
              <SectionItem
                key={sectionConfig.id}
                section={section}
                sectionConfig={sectionConfig}
                index={index}
                totalSections={sectionsConfig.length}
                onToggle={toggleSection}
                onOptionsChange={updateSectionOptions}
                onMoveUp={moveSectionUp}
                onMoveDown={moveSectionDown}
                isExpanded={expandedSections.has(sectionConfig.id)}
                onToggleExpand={toggleExpand}
              />
            )
          })}
        </div>
      </Card>
      
      {/* Footer actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => navigate('/reports')}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          Annulla
        </button>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          Salva Template
        </button>
      </div>
    </div>
  )
}
